/**
 * LeadsTableSection — reusable leads-list body.
 *
 * Extracted from pages/OfferCalculation/LeadList.js so it can be reused in:
 *   • The standalone /app/leads page (LeadList.js — thin wrapper around this)
 *   • The Agent Performance drawer in /app/company/leads-dashboard
 *     (passed `scopeAgentId` to restrict to one agent's leads)
 *
 * Owns: filter state, fetch state, pagination state, sort state, share state,
 * mobile-Accordion expand state, search debounce, URL sync (opt-in), and
 * the entire filter-bar + DataTable + mobile-Accordion + Paginator + share
 * dialog render. Nothing else.
 *
 * Skill compliance (carried over verbatim from the original LeadList.js):
 *   - P1  IconField + InputIcon for search (no deprecated p-input-icon-left)
 *   - P2  default paginator, text-sm scale, pageLinkSize=3
 *   - P7  5-slot filter grid + hidden md:block md:col spacers
 *   - P12 lead_id_formatted column frozen left, Actions frozen right
 *   - P13 500ms search debounce, min 3 chars
 *   - P14 primitive deps in useEffect, no object refs
 *   - P17 mobile Accordion + desktop DataTable, both inside the same Card
 *   - P18 per-row Menu popup with appendTo=body, stopPropagation in mobile header
 *   - P21 Dropdown optionLabel/optionValue/showClear on every filter
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Menu } from 'primereact/menu';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Paginator } from 'primereact/paginator';
import { Skeleton } from 'primereact/skeleton';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ShareLeadDialog from '../../../components/ShareLeadDialog';
import { useMasking } from '../../../layout/context/maskingcontext';
import { maskFieldValue } from '../../../utils/maskingConfig';
import { getProcessingStatusConfig } from '../../../services/leadProcessingService';
import {
    getLeads,
    EMPLOYMENT_TYPES,
    APPLICATION_STATUSES,
} from '../../../services/offerCalculationService';

/* ─── Per-row kebab menu (P18) ────────────────────────────────────────
   One Menu instance per row, bound to rowData via closure. */
const RowActionsMenu = ({ rowData, onView, onShare, onCalculate, onEdit }) => {
    const menuRef = useRef(null);

    const items = useMemo(() => ([
        { label: 'View Details',          icon: 'pi pi-eye',        command: () => onView(rowData) },
        { label: 'Calculate Eligibility', icon: 'pi pi-calculator', command: () => onCalculate(rowData) },
        { label: 'Edit',                  icon: 'pi pi-pencil',     command: () => onEdit(rowData) },
        { separator: true },
        { label: 'Share',                 icon: 'pi pi-share-alt',  command: () => onShare(rowData) },
    ]), [rowData, onView, onShare, onCalculate, onEdit]);

    return (
        <>
            <Menu
                model={items}
                popup
                ref={menuRef}
                appendTo={typeof document !== 'undefined' ? document.body : undefined}
            />
            <Button
                icon="pi pi-ellipsis-v"
                rounded
                text
                size="small"
                aria-label="Row actions"
                onClick={(e) => menuRef.current?.toggle(e)}
            />
        </>
    );
};

/* ─── Mobile lead-card header (P17) ──────────────────────────────────── */
const LeadCardHeader = ({ lead, isMasked, onView, onShare, onCalculate, onEdit }) => {
    // The lead_data tiers are unreachable from every current endpoint, and that
    // is fine — keep them (#456). `full_name` is not a plain column read: it
    // comes from Lead::getFullNameAttribute(), which already falls back to
    // first/middle/last and then to lead_capture/lead_details inside the blob.
    // index() hiding lead_data does not disturb that, because the accessor reads
    // $this->attributes['lead_data'] rather than the serialized output. These
    // tiers only matter if a payload ever reaches here without going through the
    // model — a raw query, or a hand-built row.
    const fullName = lead.full_name
        || [lead.first_name, lead.middle_name, lead.last_name].filter(Boolean).join(' ')
        || lead.lead_data?.lead_capture?.full_name
        || lead.lead_data?.lead_details?.full_name
        || 'N/A';
    const c = getProcessingStatusConfig(lead.lead_status);
    return (
        <div className="flex justify-content-between align-items-center gap-2 w-full">
            <div className="flex flex-column min-w-0 flex-1">
                <span className="font-medium text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                    {maskFieldValue('full_name', fullName, isMasked)}
                </span>
                <span className="text-xs text-500 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                    {lead.lead_id_formatted || `#${lead.id}`}
                </span>
            </div>
            <div className="flex align-items-center gap-1 flex-shrink-0">
                <Tag value={c.label} severity={c.severity} icon={`pi ${c.icon}`} className="text-xs" />
                <div onClick={(e) => e.stopPropagation()}>
                    <RowActionsMenu
                        rowData={lead}
                        onView={onView}
                        onShare={onShare}
                        onCalculate={onCalculate}
                        onEdit={onEdit}
                    />
                </div>
            </div>
        </div>
    );
};

/* ─── Mobile lead-card expanded body (P17) — mirrors every desktop column ─ */
const LeadCardDetails = ({ lead, isMasked }) => {
    const phone = lead.phone ? maskFieldValue('phone', lead.phone, isMasked) : '—';
    const created = lead.created_at
        ? new Date(lead.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—';

    const Row = ({ icon, label, children }) => (
        <div className="flex align-items-center gap-2">
            <i className={`pi ${icon} text-500 w-1rem`} aria-hidden="true" />
            <span className="text-500 font-medium w-8rem flex-shrink-0">{label}</span>
            <span className="text-700 flex-1">{children}</span>
        </div>
    );

    return (
        <div className="flex flex-column gap-2 text-xs">
            <Row icon="pi-phone" label="Phone">{phone}</Row>
            {/* Mirrors the desktop "Loan Product" column (P17 — the expanded
                card must not drop information the table shows). */}
            <Row icon="pi-briefcase" label="Loan Product">
                {lead.loan_product_name || lead.loan_type_name || <span className="text-500">—</span>}
            </Row>
            <Row icon="pi-share-alt" label="Shared">
                {lead.is_shared
                    ? <Tag value="Yes" severity="success" className="text-xs" />
                    : <Tag value="No" severity="secondary" className="text-xs" />}
            </Row>
            <Row icon="pi-calendar" label="Created">{created}</Row>
        </div>
    );
};

/* ─── Main section ──────────────────────────────────────────────────── */

const LeadsTableSection = ({
    /**
     * Optional fetcher override. When provided, the section calls this
     * instead of the default getLeads(). Signature:
     *   (params) => Promise<{ success, data: { data: [...], total, ... } }>
     * Used by AgentLeadsDrawer to swap the standalone-leads endpoint for
     * the company-admin agent-leads endpoint without changing any UI.
     */
    fetchFn = null,
    /**
     * When true (default), the status filter syncs to ?status= in the URL
     * so a deep link / refresh preserves the user's selection. Embedded
     * uses (drawer, modal) should pass false so they don't pollute the
     * parent page's URL state.
     */
    syncToUrl = true,
    /**
     * Optional callback invoked with the total record count whenever the
     * server returns new pagination data. Lets the parent (e.g. drawer
     * header) render "28 leads total" without making a second request.
     */
    onTotalRecords = null,
    /**
     * When true (default), the body is wrapped in a PrimeReact <Card>
     * (correct for the standalone /app/leads page — P20). When embedded
     * in a surface that already provides chrome (the agent drawer), pass
     * false so we don't nest a Card inside a panel — that double-padding
     * eats precious horizontal room on mobile and looks like a card
     * floating inside a sheet.
     */
    wrapInCard = true,
    /**
     * When true (default), the desktop DataTable uses scrollable +
     * scrollHeight="flex" — correct for a full-page layout where the table
     * scrolls within the viewport. Inside a drawer/dialog that flex-scroll
     * collapses and fights the panel's own scroll, so the embedded use
     * passes false: the table renders at natural height and the single
     * scroll container (the drawer body) handles overflow.
     */
    tableScrollable = true,
}) => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const toast = useRef(null);

    // Masking
    const { isMasked } = useMasking();

    // Data state
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);

    // Table state
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [sortField, setSortField] = useState('created_at');
    const [sortOrder, setSortOrder] = useState(-1);

    // Share state
    const [shareDialogVisible, setShareDialogVisible] = useState(false);
    const [leadToShare, setLeadToShare] = useState(null);

    // Mobile accordion state (P17)
    const [mobileActiveIndex, setMobileActiveIndex] = useState(null);

    // Filter state — initialize from URL params iff syncToUrl
    const [globalFilter, setGlobalFilter] = useState('');
    const [debouncedGlobalFilter, setDebouncedGlobalFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState(
        syncToUrl ? (searchParams.get('status') || null) : null
    );
    const [employmentTypeFilter, setEmploymentTypeFilter] = useState(null);

    // Status options for filter dropdown
    const statusOptions = useMemo(() => [
        { label: 'All Statuses', value: null },
        ...Object.values(APPLICATION_STATUSES).map((s) => ({ label: s.label, value: s.value })),
    ], []);

    // Employment type options for filter dropdown
    const employmentTypeOptions = useMemo(() => [
        { label: 'All Types', value: null },
        ...EMPLOYMENT_TYPES.map((t) => ({ label: t.label, value: t.value })),
    ], []);

    // Sync status filter to URL params — opt-in via syncToUrl
    useEffect(() => {
        if (!syncToUrl) return;
        const params = new URLSearchParams(searchParams);
        if (statusFilter) {
            params.set('status', statusFilter);
        } else {
            params.delete('status');
        }
        setSearchParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, syncToUrl]);

    // Debounce search — trigger after 3 characters with 500ms delay
    useEffect(() => {
        if (globalFilter === '') {
            setDebouncedGlobalFilter('');
            return;
        }
        if (globalFilter.length < 3) {
            setDebouncedGlobalFilter('');
            return;
        }
        const timer = setTimeout(() => {
            setDebouncedGlobalFilter(globalFilter);
            setFirst(0);
        }, 500);
        return () => clearTimeout(timer);
    }, [globalFilter]);

    // Load leads function
    const loadLeads = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: Math.floor(first / rows) + 1,
                per_page: rows,
                sort_by: sortField,
                sort_order: sortOrder === 1 ? 'asc' : 'desc',
            };

            const trimmedSearch = debouncedGlobalFilter.trim();
            if (trimmedSearch) params.search = trimmedSearch;
            if (statusFilter) params.status = statusFilter;
            if (employmentTypeFilter) params.employment_type = employmentTypeFilter;

            const response = fetchFn ? await fetchFn(params) : await getLeads(params);

            if (response.success) {
                const paginatedData = response.data || {};
                setLeads(paginatedData.data || []);
                const total = paginatedData.total || 0;
                setTotalRecords(total);
                if (onTotalRecords) onTotalRecords(total);
            } else {
                throw new Error(response.message || 'Failed to load leads');
            }
        } catch (error) {
            console.error('Error loading leads:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary:  'Error',
                detail:   error?.message || 'Failed to load leads',
                life:     3000,
            });
            setLeads([]);
            setTotalRecords(0);
            if (onTotalRecords) onTotalRecords(0);
        } finally {
            setLoading(false);
        }
    }, [first, rows, sortField, sortOrder, debouncedGlobalFilter, statusFilter, employmentTypeFilter, fetchFn, onTotalRecords]);

    // Load leads when filters change
    useEffect(() => {
        loadLeads();
    }, [loadLeads]);

    // Reset mobile expanded row when result set changes (P17)
    useEffect(() => {
        setMobileActiveIndex(null);
    }, [first, debouncedGlobalFilter, statusFilter, employmentTypeFilter, fetchFn]);

    // Event handlers
    const onPage = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    const onSort = (event) => {
        setSortField(event.sortField);
        setSortOrder(event.sortOrder);
    };

    const onGlobalFilterChange = (e) => setGlobalFilter(e.target.value);

    const clearFilters = () => {
        setGlobalFilter('');
        setDebouncedGlobalFilter('');
        setStatusFilter(null);
        setEmploymentTypeFilter(null);
        setFirst(0);
    };

    const viewLeadDetails = useCallback((lead) => {
        navigate(`/app/leads/${lead.id}`);
    }, [navigate]);

    const handleShareLead = useCallback((lead) => {
        setLeadToShare(lead);
        setShareDialogVisible(true);
    }, []);

    const handleShareSuccess = (message) => {
        toast.current?.show(message);
        setShareDialogVisible(false);
        setLeadToShare(null);
        loadLeads();
    };

    const handleCalculate = useCallback((rowData) => {
        navigate(`/app/leads/edit/${rowData.id}?step=eligibility`);
    }, [navigate]);

    const handleEdit = useCallback((rowData) => {
        navigate(`/app/leads/edit/${rowData.id}`);
    }, [navigate]);

    // Column Templates
    const leadIdTemplate = useCallback((rowData) => (
        <span className="font-semibold">{rowData.lead_id_formatted || `#${rowData.id}`}</span>
    ), []);

    const nameBodyTemplate = useCallback((rowData) => {
        // See LeadCardHeader — the lead_data tiers are backstops behind
        // Lead::getFullNameAttribute(), not the live path (#456).
        const fullName = rowData.full_name
            || [rowData.first_name, rowData.middle_name, rowData.last_name].filter(Boolean).join(' ')
            || rowData.lead_data?.lead_capture?.full_name
            || rowData.lead_data?.lead_details?.full_name
            || 'N/A';
        const isFromWhatsApp = rowData.entry_point === 'whatsapp_flow';
        return (
            <span className="flex align-items-center gap-1">
                <span className="font-semibold">{maskFieldValue('full_name', fullName, isMasked)}</span>
                {isFromWhatsApp && (
                    <i
                        className="pi pi-whatsapp text-primary pi-sm"
                        title="Lead created from WhatsApp"
                        aria-hidden="true"
                    />
                )}
            </span>
        );
    }, [isMasked]);

    const phoneBodyTemplate = useCallback((rowData) => (
        <span>{rowData.phone ? maskFieldValue('phone', rowData.phone, isMasked) : '-'}</span>
    ), [isMasked]);

    const statusBodyTemplate = useCallback((rowData) => {
        const c = getProcessingStatusConfig(rowData.lead_status);
        return <Tag value={c.label} severity={c.severity} icon={`pi ${c.icon}`} className="text-xs" />;
    }, []);

    const dateBodyTemplate = useCallback((rowData) => {
        if (!rowData.created_at) return '-';
        return new Date(rowData.created_at).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    }, []);

    // Loan Product column. The product name is the specific offering the user
    // picked (e.g. "Business Loan for Self Employed(ABB)"); the loan type below
    // it is the broader category. Leads created before product selection — or
    // abandoned before that step — have no product, so we show the loan type
    // alone rather than an unhelpful dash.
    const loanProductTemplate = useCallback((rowData) => {
        const product = rowData.loan_product_name;
        const type = rowData.loan_type_name;

        if (!product && !type) return <span className="text-400">—</span>;

        if (!product) {
            return (
                <span className="text-sm text-700" title={`Loan type: ${type}`}>
                    {type}
                </span>
            );
        }

        return (
            <div className="flex flex-column" style={{ lineHeight: 1.2 }}>
                <span className="text-sm font-medium text-900" title={product}>{product}</span>
                {type && <span className="text-xs text-500">{type}</span>}
            </div>
        );
    }, []);

    const createdByTemplate = useCallback((rowData) => {
        const userType = rowData.created_by_user_type;
        const email = rowData.created_by_email;
        if (!userType && !email) return <span className="text-400">—</span>;
        const labels = {
            company_owner: 'Company Owner',
            company_user:  'Company User',
            direct_user:   'Direct User',
            owner:         'Tenant Owner',
        };
        const typeLabel = labels[userType] || userType || '—';
        return (
            <div className="flex flex-column" style={{ lineHeight: 1.2 }}>
                <span className="text-sm font-medium text-900">{typeLabel}</span>
                {email && <span className="text-xs text-500" title={email}>{email}</span>}
            </div>
        );
    }, []);

    const sharedStatusTemplate = useCallback((rowData) => (
        <div className="flex align-items-center">
            {rowData.is_shared ? (
                <>
                    <Tag value="Yes" severity="success" className="text-xs" />
                    <i className="pi pi-share-alt ml-2 text-500 pi-sm" aria-hidden="true"></i>
                </>
            ) : (
                <>
                    <Tag value="No" severity="secondary" className="text-xs" />
                    <i className="pi pi-lock ml-2 text-400 pi-sm" aria-hidden="true"></i>
                </>
            )}
        </div>
    ), []);

    const actionBodyTemplate = useCallback((rowData) => (
        <div className="flex justify-content-center">
            <RowActionsMenu
                rowData={rowData}
                onView={viewLeadDetails}
                onShare={handleShareLead}
                onCalculate={handleCalculate}
                onEdit={handleEdit}
            />
        </div>
    ), [viewLeadDetails, handleShareLead, handleCalculate, handleEdit]);

    // ── Activity column ─────────────────────────────────────────────────
    // Single OverlayPanel reused across rows (PrimeReact pattern — keeping
    // one panel and re-targeting it on click avoids mounting N panels per
    // page render). The currently-viewed row's activity sits in state.
    const activityOpRef = useRef(null);
    const [activityRow, setActivityRow] = useState(null);

    const formatActivityTime = useCallback((iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: '2-digit',
            hour: '2-digit', minute: '2-digit',
        });
    }, []);

    const activityBodyTemplate = useCallback((rowData) => {
        const summary = rowData.activity_summary;
        const counts = summary?.counts || { edits: 0, verifies: 0, regens: 0, claims: 0 };
        const total = counts.edits + counts.verifies + counts.regens + counts.claims;

        if (total === 0) {
            return <span className="text-400 text-xs">—</span>;
        }

        const chips = [
            { key: 'edits',    icon: 'pi-pencil',        count: counts.edits,    color: 'text-blue-600',   title: 'Offers edited' },
            { key: 'verifies', icon: 'pi-check-circle',  count: counts.verifies, color: 'text-green-600',  title: 'Offers verified' },
            { key: 'regens',   icon: 'pi-refresh',       count: counts.regens,   color: 'text-orange-600', title: 'Offers regenerated' },
            { key: 'claims',   icon: 'pi-hand-point-up', count: counts.claims,   color: 'text-purple-600', title: 'Claims placed' },
        ].filter((c) => c.count > 0);

        return (
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setActivityRow(rowData);
                    activityOpRef.current?.toggle(e);
                }}
                className="flex align-items-center gap-2 p-1 border-none surface-0 cursor-pointer hover:surface-100 border-round"
                title="Click to view recent activity"
            >
                {chips.map((c) => (
                    <span key={c.key} className={`flex align-items-center gap-1 text-xs ${c.color}`} title={c.title}>
                        <i className={`pi ${c.icon}`} style={{ fontSize: '0.75rem' }} aria-hidden="true" />
                        {c.count}
                    </span>
                ))}
            </button>
        );
    }, []);

    // Card on the standalone page (P20); bare fragment when embedded in a
    // surface that already provides chrome (the agent drawer).
    const Wrapper = wrapInCard ? Card : React.Fragment;

    return (
        <>
            <Toast ref={toast} />
            <Wrapper>
                {/* Filter Bar — 5-slot equal-space grid (P7) */}
                <div className="surface-ground p-3 border-round mb-3">
                    <div className="grid">
                        {/* Slot 1 — Search */}
                        <div className="col-12 md:col">
                            <IconField iconPosition="left" className="w-full">
                                <InputIcon className="pi pi-search" />
                                <InputText
                                    value={globalFilter}
                                    onChange={onGlobalFilterChange}
                                    placeholder="Search name, phone (min 3 chars)"
                                    maxLength={100}
                                    className="w-full p-inputtext-sm"
                                />
                            </IconField>
                        </div>

                        {/* Slot 2 — Status */}
                        <div className="col-6 md:col">
                            <Dropdown
                                value={statusFilter}
                                options={statusOptions}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => { setStatusFilter(e.value); setFirst(0); }}
                                placeholder="All Statuses"
                                showClear
                                className="w-full p-inputtext-sm"
                            />
                        </div>

                        {/* Slot 3 — Employment Type */}
                        <div className="col-6 md:col">
                            <Dropdown
                                value={employmentTypeFilter}
                                options={employmentTypeOptions}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => { setEmploymentTypeFilter(e.value); setFirst(0); }}
                                placeholder="All Types"
                                showClear
                                className="w-full p-inputtext-sm"
                            />
                        </div>

                        {/* Slot 4 — invisible spacer */}
                        <div className="hidden md:block md:col" aria-hidden="true"></div>

                        {/* Slot 5 — Action block */}
                        <div className="col-12 md:col flex gap-2">
                            <Button
                                label="Clear"
                                icon="pi pi-filter-slash"
                                severity="secondary"
                                outlined
                                size="small"
                                className="flex-1 py-1 md:py-2"
                                onClick={clearFilters}
                            />
                            <Button
                                label="Refresh"
                                icon="pi pi-refresh"
                                size="small"
                                className="flex-1 py-1 md:py-2"
                                onClick={loadLeads}
                            />
                        </div>
                    </div>
                </div>

                {/* Mobile (< md) — Accordion cards (P17) */}
                <div className="block md:hidden">
                    {/* Loading state — without this the mobile branch renders
                        blank during the initial fetch (the desktop DataTable
                        has its own `loading` overlay; the Accordion path needs
                        its own). Skeleton rows mirror the collapsed card height. */}
                    {loading && leads.length === 0 && (
                        <div className="flex flex-column gap-2 py-2">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <Skeleton key={i} height="3.25rem" borderRadius="8px" />
                            ))}
                        </div>
                    )}
                    {!loading && leads.length === 0 && (
                        <div className="flex flex-column align-items-center py-5">
                            <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                            <span className="text-600">No leads found.</span>
                        </div>
                    )}
                    {leads.length > 0 && (
                        <>
                            <Accordion
                                activeIndex={mobileActiveIndex}
                                onTabChange={(e) => setMobileActiveIndex(e.index)}
                            >
                                {leads.map((lead) => (
                                    <AccordionTab
                                        key={lead.id}
                                        header={<LeadCardHeader
                                            lead={lead}
                                            isMasked={isMasked}
                                            onView={viewLeadDetails}
                                            onShare={handleShareLead}
                                            onCalculate={handleCalculate}
                                            onEdit={handleEdit}
                                        />}
                                    >
                                        <LeadCardDetails lead={lead} isMasked={isMasked} />
                                    </AccordionTab>
                                ))}
                            </Accordion>
                            <Paginator
                                first={first}
                                rows={rows}
                                totalRecords={totalRecords}
                                onPageChange={onPage}
                                template="PrevPageLink PageLinks NextPageLink CurrentPageReport"
                                currentPageReportTemplate="{first}-{last} of {totalRecords}"
                                pageLinkSize={3}
                                className="text-sm mt-2"
                            />
                        </>
                    )}
                </div>

                {/* Desktop (≥ md) — DataTable (P17) */}
                <div className="hidden md:block">
                    <DataTable
                        key={`leads-table-${isMasked}`}
                        value={leads}
                        dataKey="id"
                        lazy
                        loading={loading}
                        first={first}
                        rows={rows}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        onSort={onSort}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        paginator
                        rowsPerPageOptions={[10, 25, 50]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                        currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                        paginatorClassName="text-sm"
                        pageLinkSize={3}
                        size="small"
                        className="p-datatable-gridlines"
                        showGridlines
                        stripedRows
                        responsiveLayout="scroll"
                        scrollable={tableScrollable}
                        scrollHeight={tableScrollable ? 'flex' : undefined}
                        emptyMessage={
                            <div className="flex flex-column align-items-center py-5">
                                <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                                <span className="text-600">No leads found.</span>
                            </div>
                        }
                    >
                        <Column
                            field="lead_id_formatted"
                            header="Lead ID"
                            body={leadIdTemplate}
                            sortable
                            frozen
                            alignFrozen="left"
                            style={{ minWidth: '110px' }}
                        />
                        <Column
                            field="full_name"
                            header="Name"
                            body={nameBodyTemplate}
                            sortable
                            style={{ minWidth: '150px' }}
                        />
                        <Column
                            field="phone"
                            header="Phone"
                            body={phoneBodyTemplate}
                            sortable
                            style={{ width: '130px' }}
                        />
                        <Column
                            field="lead_status"
                            header="Status"
                            body={statusBodyTemplate}
                            sortable
                            style={{ width: '150px' }}
                        />
                        <Column
                            field="loan_product_name"
                            header="Loan Product"
                            body={loanProductTemplate}
                            sortable
                            style={{ minWidth: '200px' }}
                        />
                        <Column
                            field="is_shared"
                            header="Shared"
                            body={sharedStatusTemplate}
                            style={{ width: '100px' }}
                        />
                        <Column
                            field="created_by_user_type"
                            header="Created By"
                            body={createdByTemplate}
                            style={{ minWidth: '180px' }}
                        />
                        <Column
                            field="created_at"
                            header="Created"
                            body={dateBodyTemplate}
                            sortable
                            style={{ width: '110px' }}
                        />
                        <Column
                            header="Activity"
                            body={activityBodyTemplate}
                            style={{ width: '170px' }}
                        />
                        <Column
                            body={actionBodyTemplate}
                            exportable={false}
                            frozen
                            header="Actions"
                            alignFrozen="right"
                            style={{ minWidth: '70px' }}
                        />
                    </DataTable>
                </div>
            </Wrapper>

            {/* Activity timeline overlay — shared singleton, retargeted on
                click. Reads from the currently-clicked rowData's
                activity_summary.recent (top 5 across edits/verifies/
                regens/claims, newest first). */}
            <OverlayPanel
                ref={activityOpRef}
                style={{ width: '320px' }}
                onHide={() => setActivityRow(null)}
            >
                {activityRow ? (() => {
                    const recent = activityRow.activity_summary?.recent || [];
                    if (recent.length === 0) {
                        return <div className="text-sm text-500">No recent activity.</div>;
                    }
                    const kindStyle = {
                        edits:    { icon: 'pi-pencil',       color: 'text-blue-600',   label: 'Edited' },
                        verifies: { icon: 'pi-check-circle', color: 'text-green-600',  label: 'Verified' },
                        regens:   { icon: 'pi-refresh',      color: 'text-orange-600', label: 'Regenerated' },
                        claims:   { icon: 'pi-hand-point-up',color: 'text-purple-600', label: 'Claimed' },
                    };
                    return (
                        <div className="flex flex-column gap-2">
                            <div className="text-xs font-semibold text-700 uppercase mb-1">
                                Recent activity — {activityRow.lead_id_formatted}
                            </div>
                            {recent.map((row, i) => {
                                const style = kindStyle[row.kind] || { icon: 'pi-circle', color: 'text-500', label: row.kind };
                                const extra = row.bank
                                    ? <span className="text-500">{` · ${row.bank}`}</span>
                                    : row.status
                                        ? <span className="text-500">{` · ${row.status}`}</span>
                                        : null;
                                return (
                                    <div key={i} className="flex align-items-start gap-2 text-xs">
                                        <i className={`pi ${style.icon} ${style.color} mt-1`} style={{ fontSize: '0.7rem' }} aria-hidden="true" />
                                        <div className="flex-1">
                                            <div className="text-700">
                                                <span className={`${style.color} font-medium`}>{style.label}</span>
                                                {' by '}
                                                <span className="font-medium text-900">{row.by || 'Unknown'}</span>
                                                {extra}
                                            </div>
                                            <div className="text-500">{formatActivityTime(row.at)}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })() : null}
            </OverlayPanel>

            {/* Share Lead Dialog */}
            {leadToShare && (
                <ShareLeadDialog
                    visible={shareDialogVisible}
                    onHide={() => {
                        setShareDialogVisible(false);
                        setLeadToShare(null);
                    }}
                    lead={leadToShare}
                    onSuccess={handleShareSuccess}
                />
            )}
        </>
    );
};

export default LeadsTableSection;
